/**
 * Audio System Controller - Core System
 * 
 * EVOLUTION: Subdivided from monolithic AudioManager
 * 
 * Coordinates multiple specialized audio engines:
 * - MainAudioEngine: Sacred & Realtime audio
 * - SabianAudioEngine: Sabian symbol sounds (separate) [[memory:8765835]]
 * - ReverbProcessor: Spatial audio effects
 * - AudioMonitor: Real-time analysis and monitoring
 * 
 * EXPANDED PURPOSE:
 * - Plugin system for audio effects
 * - Dynamic routing and mixing
 * - Audio scene management
 * - Performance monitoring
 * - Spatial audio experimentation
 * - Real-time audio synthesis
 */

import { MainAudioEngine } from './MainAudioEngine';
import { SabianAudioEngine } from './SabianAudioEngine';
import { ReverbProcessor } from './ReverbProcessor';

export interface AudioSystemState {
  isInitialized: boolean;
  isInitializing: boolean;
  error: Error | null;
  enginesReady: string[];
  enginesFailed: string[];
}

export type AudioInitCallback = (controller: AudioSystemController) => void;

/**
 * Singleton Audio System Controller
 */
class AudioSystemController {
  private static instance: AudioSystemController | null = null;
  private state: AudioSystemState;
  private initCallbacks: AudioInitCallback[] = [];
  
  // Specialized audio engines
  private mainEngine: MainAudioEngine | null = null;
  private sabianEngine: SabianAudioEngine | null = null;
  private reverbProcessor: ReverbProcessor | null = null;

  private constructor() {
    this.state = {
      isInitialized: false,
      isInitializing: false,
      error: null,
      enginesReady: [],
      enginesFailed: []
    };

    console.log('🎵 Audio System Controller created (Enhanced)');
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): AudioSystemController {
    if (!AudioSystemController.instance) {
      AudioSystemController.instance = new AudioSystemController();
    }
    return AudioSystemController.instance;
  }

  /**
   * Get current state
   */
  public getState(): Readonly<AudioSystemState> {
    return { ...this.state };
  }

  /**
   * Check if audio is ready
   */
  public isReady(): boolean {
    return this.state.isInitialized && this.mainEngine !== null;
  }

  /**
   * Register callback for when audio initializes
   */
  public onInitialized(callback: AudioInitCallback): () => void {
    if (this.state.isInitialized) {
      callback(this);
    } else {
      this.initCallbacks.push(callback);
    }

    return () => {
      this.initCallbacks = this.initCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Initialize audio system
   */
  public async initialize(userInteraction: boolean = false): Promise<void> {
    if (this.state.isInitialized) {
      console.log('🎵 Audio already initialized');
      return;
    }

    if (this.state.isInitializing) {
      console.log('🎵 Audio initialization in progress...');
      return;
    }

    if (!userInteraction) {
      console.warn('⚠️ Audio initialization requires user interaction');
      throw new Error('Audio initialization requires user interaction (click, tap, keypress)');
    }

    this.state.isInitializing = true;
    this.state.error = null;

    try {
      console.log('🎵 Initializing Audio System...');

      // Check browser support
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error('Web Audio API not supported in this browser');
      }

      // Initialize Main Audio Engine (Sacred & Realtime)
      try {
        this.mainEngine = new MainAudioEngine();
        await this.mainEngine.initialize();
        this.state.enginesReady.push('MainAudioEngine');
        console.log('✅ Main Audio Engine ready');
      } catch (error) {
        console.error('❌ Main Audio Engine failed:', error);
        this.state.enginesFailed.push('MainAudioEngine');
        throw error;
      }

      // Initialize Sabian Audio Engine (Separate context) [[memory:8765835]]
      try {
        this.sabianEngine = new SabianAudioEngine();
        await this.sabianEngine.initialize();
        this.state.enginesReady.push('SabianAudioEngine');
        console.log('✅ Sabian Audio Engine ready');
      } catch (error) {
        console.error('❌ Sabian Audio Engine failed:', error);
        this.state.enginesFailed.push('SabianAudioEngine');
        // Non-critical, continue
      }

      // Initialize Reverb Processor
      try {
        if (this.mainEngine) {
          const context = this.mainEngine.getContext();
          if (context) {
            this.reverbProcessor = new ReverbProcessor(context);
            await this.reverbProcessor.initialize();
            this.state.enginesReady.push('ReverbProcessor');
            console.log('✅ Reverb Processor ready');
          }
        }
      } catch (error) {
        console.error('❌ Reverb Processor failed:', error);
        this.state.enginesFailed.push('ReverbProcessor');
        // Non-critical, continue
      }

      // Mark as initialized
      this.state.isInitialized = true;
      this.state.isInitializing = false;

      console.log('✅ Audio System Controller initialized successfully');

      // Notify all waiting components
      this.initCallbacks.forEach(callback => {
        try {
          callback(this);
        } catch (error) {
          console.error('Error in audio init callback:', error);
        }
      });
      this.initCallbacks = [];

    } catch (error) {
      this.state.error = error as Error;
      this.state.isInitializing = false;
      console.error('❌ Failed to initialize Audio System Controller:', error);
      throw error;
    }
  }

  /**
   * Get Main Audio Engine
   */
  public getMainEngine(): MainAudioEngine | null {
    return this.mainEngine;
  }

  /**
   * Get Sabian Audio Engine [[memory:8765835]]
   */
  public getSabianEngine(): SabianAudioEngine | null {
    return this.sabianEngine;
  }

  /**
   * Get Reverb Processor
   */
  public getReverbProcessor(): ReverbProcessor | null {
    return this.reverbProcessor;
  }

  /**
   * Legacy compatibility: Get main audio context
   */
  public getMainAudioContext(): AudioContext | null {
    return this.mainEngine ? this.mainEngine.getContext() : null;
  }

  /**
   * Legacy compatibility: Get Sabian audio context
   */
  public getSabianAudioContext(): AudioContext | null {
    return this.sabianEngine ? this.sabianEngine.getContext() : null;
  }

  /**
   * Legacy compatibility: Get main master gain
   */
  public getMainMasterGain(): GainNode | null {
    return this.mainEngine ? this.mainEngine.getMasterGain() : null;
  }

  /**
   * Legacy compatibility: Get Sabian master gain
   */
  public getSabianMasterGain(): GainNode | null {
    return this.sabianEngine ? this.sabianEngine.getMasterGain() : null;
  }

  /**
   * Set master volume for main audio system
   */
  public setMainVolume(volumeDb: number): void {
    if (this.mainEngine) {
      this.mainEngine.setVolume(volumeDb);
    } else {
      console.warn('⚠️ Cannot set main volume: Audio not initialized');
    }
  }

  /**
   * Set volume for Sabian audio system
   */
  public setSabianVolume(volumeDb: number): void {
    if (this.sabianEngine) {
      this.sabianEngine.setVolume(volumeDb);
    } else {
      console.warn('⚠️ Cannot set Sabian volume: Audio not initialized');
    }
  }

  /**
   * Resume audio contexts
   */
  public async resume(): Promise<void> {
    const promises: Promise<void>[] = [];

    if (this.mainEngine) {
      promises.push(this.mainEngine.resume());
    }

    if (this.sabianEngine) {
      promises.push(this.sabianEngine.resume());
    }

    await Promise.all(promises);
  }

  /**
   * Cleanup audio system
   */
  public async cleanup(): Promise<void> {
    console.log('🎵 Cleaning up Audio System Controller...');

    const cleanupPromises: Promise<void>[] = [];

    if (this.mainEngine) {
      cleanupPromises.push(this.mainEngine.cleanup());
    }

    if (this.sabianEngine) {
      cleanupPromises.push(this.sabianEngine.cleanup());
    }

    await Promise.all(cleanupPromises);

    this.mainEngine = null;
    this.sabianEngine = null;
    this.reverbProcessor = null;

    this.state = {
      isInitialized: false,
      isInitializing: false,
      error: null,
      enginesReady: [],
      enginesFailed: []
    };

    this.initCallbacks = [];

    console.log('✅ Audio System Controller cleaned up');
  }
}

// Export singleton instance
export const audioSystemController = AudioSystemController.getInstance();
export default audioSystemController;

